import { useEffect, useState, type JSX } from 'react'
import { api } from '../../../api/client.js'
import { formatDateTime } from '../../../utils/formatDate.js'

/**
 * RunHistoryPane
 * ---------------
 * Read-only list of the most recent runs for a single scheduled report
 * config. The backend route (`GET /reports/scheduled-configs/:id/runs`) is
 * not a CRUD resource, so we don't use `useAdminResource` — a small inline
 * `useEffect + api.get` is enough.
 *
 * Spec: UI-6a Task 6. Rendered in edit-mode of ScheduledReportDrawer.
 */

export type ScheduledReportRunStatus = 'SCHEDULED' | 'RUNNING' | 'COMPLETED' | 'FAILED'

export interface ScheduledReportRun {
  id: string
  configId: string
  status: ScheduledReportRunStatus
  runAt: string
  generatedAt: string | null
  downloadUrl: string | null
  errorMessage: string | null
}

interface RunsResponse {
  runs: ScheduledReportRun[]
}

export interface RunHistoryPaneProps {
  configId: string
}

function StatusIcon({ status }: { status: ScheduledReportRunStatus }): JSX.Element {
  if (status === 'COMPLETED') {
    return (
      <span aria-label="Completed" className="text-green-600" data-testid="status-icon-completed">
        ✓
      </span>
    )
  }
  if (status === 'FAILED') {
    return (
      <span aria-label="Failed" className="text-red-600" data-testid="status-icon-failed">
        ✗
      </span>
    )
  }
  // RUNNING or SCHEDULED
  return (
    <span aria-label={status} className="text-blue-600" data-testid={`status-icon-${status.toLowerCase()}`}>
      ⏳
    </span>
  )
}

export default function RunHistoryPane(props: RunHistoryPaneProps): JSX.Element {
  const [runs, setRuns] = useState<ScheduledReportRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const res = await api.get<RunsResponse>(`/reports/scheduled-configs/${props.configId}/runs`)
        if (!cancelled) {
          setRuns(res.runs ?? [])
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load runs')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [props.configId])

  return (
    <section aria-label="Run History" className="flex flex-col gap-2">
      <h3 className="text-base font-semibold">Run History</h3>

      {loading ? (
        <div data-testid="run-history-skeleton" className="flex flex-col gap-2" aria-busy="true">
          <div className="h-8 w-full animate-pulse rounded bg-gray-100" />
          <div className="h-8 w-full animate-pulse rounded bg-gray-100" />
          <div className="h-8 w-full animate-pulse rounded bg-gray-100" />
        </div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-600">
          {error}
        </div>
      ) : runs.length === 0 ? (
        <div className="text-sm text-gray-500">No runs yet.</div>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100">
          {runs.map((run) => (
            <li
              key={run.id}
              role="status"
              className="flex items-start justify-between gap-3 py-2 text-sm"
            >
              <div className="flex items-start gap-2">
                <StatusIcon status={run.status} />
                <div className="flex flex-col">
                  <span className="text-gray-900">{formatDateTime(run.runAt)}</span>
                  {run.errorMessage ? (
                    <span className="text-xs text-red-600">{run.errorMessage}</span>
                  ) : null}
                </div>
              </div>
              {run.downloadUrl ? (
                <a
                  href={run.downloadUrl}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  Download
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
